import React, { useState, useEffect } from 'react';
import { Modal, Spin, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useMount, useRequest } from 'ahooks';
import { api } from '@/redux/api';
import { useFacilityId } from '@/hooks/use-facilityid';
import { useKeepAliveContext } from '@/hooks/use-keep-alive-context';
import { emitEvent, EventName, useEventBus } from '@/hooks/useEventBus';
import moment from 'moment';
import UpdateEUI from '../UpdateEUI';
import {
  PageWrapper,
  Container,
  Header,
  ProgressBarWrapper,
  ProgressLine,
  ProgressStep,
  Content,
  StatusCard,
  Button,
  InfoCard,
  InfoRow,
  CertItems,
  CertItem,
  CertItemLeft,
  CertStatus,
  StatusBadge,
  ComparisonTable,
  SuccessAnimation,
  Spinner,
  DebugPanel
} from './index.style';

// ==================== Main Component ====================
const CertifyPage = () => {
  const STATES = {
    INITIAL: 'INITIAL',
    PENDING_ASSIGNMENT: 'PENDING_ASSIGNMENT',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
    SUCCESS: 'SUCCESS'
  };

  const STATE_TO_STEP = {
    'INITIAL': 1,
    'PENDING_ASSIGNMENT': 2,
    'IN_PROGRESS': 3,
    'COMPLETED': 4,
    'SUCCESS': 5
  };

  const navigate = useNavigate();
  const facility_id = useFacilityId();
  const { refreshTab } = useKeepAliveContext();
  const [currentState, setCurrentState] = useState(STATES.INITIAL);

  // Get certification status data (Gas/Elec)
  const { data: certificationData = {
    electricity: { total: 0, certified: 0, percentage: 0 },
    gas: { total: 0, certified: 0, percentage: 0 },
    overall: { total: 0, certified: 0, percentage: 0 }
  }, loading: certificationDataLoading, run: runCertificationData } = useRequest(() => api.getEUICertificationStatus(), {
    manual: true
  });

  // Get certification task information
  const { data: certifyInfo = {}, loading: certifyInfoLoading, run: runCertifyInfo } = useRequest(async () => {
    const res = await api.getCertifyRequestsById();
    if (res.id) {
      return api.getCertifyLogs(res.id);
    }
  }, {
    manual: true
  });

  const isLoading = certifyInfoLoading || certificationDataLoading;

  // Initialize state based on data
  const initStep = (certifyInfo, certificationData) => {
    if (!certifyInfo?.task) {
      setCurrentState(STATES.INITIAL);
      return;
    }
    const isAllDataCertified = certificationData.electricity.certified === certificationData.electricity.total && certificationData.gas.certified === certificationData.gas.total;

    // If task is completed
    if (certifyInfo?.task?.status === 'Completed') {
      if (isAllDataCertified) {
        setCurrentState(STATES.SUCCESS); // Completed
      } else {
        setCurrentState(STATES.INITIAL); // Restart
      }
    } 
    // If task is submitted but not assigned to Assignee yet
    else if (certifyInfo?.task?.status === 'Submitted' && !certifyInfo?.task?.certify_by?.user_id) {
      setCurrentState(STATES.PENDING_ASSIGNMENT); // Pending Assign
    }
    // Task is in progress
    else {
      if (isAllDataCertified) {
        setCurrentState(STATES.COMPLETED); // Update EUI
      } else {
        setCurrentState(STATES.IN_PROGRESS); // Pending Certification
      }
    }
  };

  useEffect(() => {
    initStep(certifyInfo, certificationData);
  }, [certifyInfo, certificationData]);

  const fetchData = () => {
    runCertificationData();
    runCertifyInfo();
  };

  useMount(() => {
    fetchData();
  });

  useEventBus(EventName.UPDATE_CERTIFICATION_STATUS, () => {
    fetchData();
  });

  const changeStatus = (newState) => {
    if (!STATES[newState]) {
      console.error('Invalid state:', newState);
      return;
    }
    console.log(`State transition: ${currentState} -> ${newState}`);
    setCurrentState(newState);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Request certification
  const requestCertification = () => {
    api.requestEUITask(facility_id).then(() => {
      refreshTab();
      fetchData();
      emitEvent(EventName.REFRESH_CERTIFY_REQUEST);
      message.success('Certification request submitted successfully');
    }).catch(() => {
      message.error('Failed to submit certification request');
    });
  };

  // Update EUI
  const updateEUI = (euidata) => {
    Modal.confirm({
      title: 'Confirm Update',
      content: 'Are you sure you want to update the EUI data to Benchmark?',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => {
        api.updateBenchmarkEUI(facility_id, euidata, certifyInfo.task.id).then(res => {
          if (res.status === 200) {
            fetchData();
            emitEvent(EventName.REFRESH_CERTIFY_REQUEST);
            message.success('EUI data updated successfully');
          }
        }).catch(() => {
          message.error('Failed to update EUI data');
        });
      }
    });
  };

  // Recertify confirmation
  const handleRecertify = () => {
    Modal.confirm({
      title: "Recertify?",
      content: "All data has been certified and EUI data is current. Do you still want to recertify?",
      okText: "Yes",
      cancelText: "No",
      onOk: requestCertification,
    });
  };

  // Navigate to Gas or Elec page
  const goToPage = (pageType) => {
    let baseUrl = '';
    if (pageType === 'elec') {
      emitEvent(EventName.CLOSE_ELECTRICITY);
      baseUrl = '/facility/Electricity';
    } else if (pageType === 'gas') {
      emitEvent(EventName.CLOSE_GAS);
      baseUrl = '/facility/Gas';
    }
    navigate(baseUrl);
  };

  const getProgressWidth = () => {
    const currentStep = STATE_TO_STEP[currentState] || 1;
    return ((currentStep - 1) / 4) * 100;
  };

  const isStepActive = (step) => STATE_TO_STEP[currentState] === step;
  const isStepCompleted = (step) => STATE_TO_STEP[currentState] > step;

  return (
    <Spin spinning={isLoading} tip="Loading certification data..." size="large">
      <PageWrapper>
        <Container>
          <Header>
            <h1>🔐 Certification Management</h1>
            <p className="subtitle">Data Certification Process Management System</p>
          </Header>

        {/* Progress Bar */}
        <ProgressBarWrapper>
          <ProgressLine width={getProgressWidth()} />
          <ProgressStep active={isStepActive(1)} completed={isStepCompleted(1)}>
            <div className="step-circle">1</div>
            <div className="step-label">Request</div>
          </ProgressStep>
          <ProgressStep active={isStepActive(2)} completed={isStepCompleted(2)}>
            <div className="step-circle">2</div>
            <div className="step-label">Pending</div>
          </ProgressStep>
          <ProgressStep active={isStepActive(3)} completed={isStepCompleted(3)}>
            <div className="step-circle">3</div>
            <div className="step-label">In Progress</div>
          </ProgressStep>
          <ProgressStep active={isStepActive(4)} completed={isStepCompleted(4)}>
            <div className="step-circle">4</div>
            <div className="step-label">Update EUI</div>
          </ProgressStep>
          <ProgressStep active={isStepActive(5)} completed={isStepCompleted(5)}>
            <div className="step-circle">5</div>
            <div className="step-label">Completed</div>
          </ProgressStep>
        </ProgressBarWrapper>

        <Content>
          {/* State 1: Initial */}
          <StatusCard active={currentState === STATES.INITIAL}>
            <div className="status-icon">📋</div>
            <h2 className="status-title">Ready to Request Data Certification?</h2>
            <p className="status-description">
              Click the button below to initiate the certification process for your utility data.<br />
              The request will be submitted to an administrator who will assign a certification specialist.
            </p>
            <Button variant="primary" onClick={requestCertification}>
              🚀 Request Certification
            </Button>
          </StatusCard>

          {/* State 2: Pending Assignment */}
          <StatusCard active={currentState === STATES.PENDING_ASSIGNMENT}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <h2 className="status-title">Waiting for Task Assignment</h2>
            <p className="status-description">
              Certification request has been submitted successfully!<br />
              The system is waiting for an administrator to assign a certification specialist (Assignee)...
            </p>
            <InfoCard>
              <InfoRow>
                <span className="info-label">Requested By</span>
                <span className="info-value">{certifyInfo.task?.request_by?.user_name || 'N/A'}</span>
              </InfoRow>
              <InfoRow>
                <span className="info-label">Current Status</span>
                <span className="info-value">⏳ Pending Assignment</span>
              </InfoRow>
            </InfoCard>
          </StatusCard>

          {/* State 3: In Progress */}
          <StatusCard active={currentState === STATES.IN_PROGRESS}>
            <div className="status-icon">⚙️</div>
            <h2 className="status-title">Certify Pending Data</h2>
            <p className="status-description">
              There are <span style={{ color: '#1890ff', fontWeight: 'bold', fontSize: '1.1em' }}>{certificationData.electricity.total - certificationData.electricity.certified}</span> Electricity and <span style={{ color: '#1890ff', fontWeight: 'bold', fontSize: '1.1em' }}>{certificationData.gas.total - certificationData.gas.certified}</span> Gas data entries pending certification.<br />
              Please visit the respective pages to certify them.
            </p>

            <InfoCard style={{ marginBottom: '20px' }}>
              <InfoRow>
                <span className="info-label">Requested By</span>
                <span className="info-value">{certifyInfo.task?.request_by?.user_name || 'N/A'}</span>
              </InfoRow>
              <InfoRow>
                <span className="info-label">Assigned To</span>
                <span className="info-value">{certifyInfo.task?.certify_by?.user_name || 'N/A'}</span>
              </InfoRow>
              <InfoRow>
                <span className="info-label">Assigned By</span>
                <span className="info-value">{certifyInfo.task?.received_by?.user_name || 'N/A'}</span>
              </InfoRow>
              <InfoRow>
                <span className="info-label">Current Status</span>
                <span className="info-value">🔄 In Progress</span>
              </InfoRow>
            </InfoCard>

            <CertItems>
              <CertItem>
                <CertItemLeft>
                  <div className="cert-item-icon">🔥</div>
                  <div className="cert-item-info">
                    <h3>Gas Data Certification</h3>
                    <p>Natural gas usage data certification</p>
                  </div>
                </CertItemLeft>
                <CertStatus>
                  <StatusBadge completed={certificationData.gas.certified === certificationData.gas.total}>
                    {certificationData.gas.certified === certificationData.gas.total ? '✓' : '⏳'} 
                    {certificationData.gas.certified === certificationData.gas.total ? ' Completed' : ' Pending'} ({certificationData.gas.certified}/{certificationData.gas.total})
                  </StatusBadge>
                  <Button variant="secondary" small onClick={() => goToPage('gas')}>Go to Page</Button>
                </CertStatus>
              </CertItem>

              <CertItem>
                <CertItemLeft>
                  <div className="cert-item-icon">⚡</div>
                  <div className="cert-item-info">
                    <h3>Electricity Data Certification</h3>
                    <p>Electricity usage data certification</p>
                  </div>
                </CertItemLeft>
                <CertStatus>
                  <StatusBadge completed={certificationData.electricity.certified === certificationData.electricity.total}>
                    {certificationData.electricity.certified === certificationData.electricity.total ? '✓' : '⏳'}
                    {certificationData.electricity.certified === certificationData.electricity.total ? ' Completed' : ' Pending'} ({certificationData.electricity.certified}/{certificationData.electricity.total})
                  </StatusBadge>
                  <Button variant="secondary" small onClick={() => goToPage('elec')}>Go to Page</Button>
                </CertStatus>
              </CertItem>
            </CertItems>
          </StatusCard>

          {/* State 4: Completed - Update EUI */}
          <StatusCard active={currentState === STATES.COMPLETED}>
            <div className="status-icon">📊</div>
            <h2 className="status-title">Certification Complete - Update EUI Data</h2>
            <p className="status-description">
              All data has been certified successfully! Please review the Benchmark data comparison below.<br />
              Once confirmed, click the "Update EUI" button to update the Benchmark page.
            </p>

            <InfoCard style={{ marginTop: '20px', marginBottom: '20px' }}>
              <InfoRow>
                <span className="info-label">Requested By</span>
                <span className="info-value">{certifyInfo.task?.request_by?.user_name || 'N/A'}</span>
              </InfoRow>
              <InfoRow>
                <span className="info-label">Assigned To</span>
                <span className="info-value">{certifyInfo.task?.certify_by?.user_name || 'N/A'}</span>
              </InfoRow>
              <InfoRow>
                <span className="info-label">Assigned By</span>
                <span className="info-value">{certifyInfo.task?.received_by?.user_name || 'N/A'}</span>
              </InfoRow>
            </InfoCard>

            {certifyInfo.task && (
              <UpdateEUI updateEUI={updateEUI} task={certifyInfo.task} />
            )}
          </StatusCard>

          {/* State 5: Success */}
          <StatusCard active={currentState === STATES.SUCCESS}>
            <SuccessAnimation />
            <h2 className="status-title">🎉 Certification Complete!</h2>
            <p className="status-description">
              Congratulations! All data has been certified and EUI data is current.<br />
              Your facility is now fully compliant and no further action is needed at this time.
            </p>

            <InfoCard>
              <InfoRow>
                <span className="info-label">Requested By</span>
                <span className="info-value">{certifyInfo.task?.request_by?.user_name || 'N/A'}</span>
              </InfoRow>
              <InfoRow>
                <span className="info-label">Assigned To</span>
                <span className="info-value">{certifyInfo.task?.certify_by?.user_name || 'N/A'}</span>
              </InfoRow>
              <InfoRow>
                <span className="info-label">Assigned By</span>
                <span className="info-value">{certifyInfo.task?.received_by?.user_name || 'N/A'}</span>
              </InfoRow>
              <InfoRow>
                <span className="info-label">Final Status</span>
                <span className="info-value">✅ Completed</span>
              </InfoRow>
            </InfoCard>

            <div style={{ marginTop: '30px' }}>
              <Button variant="success" onClick={() => navigate('/facility/inter-company-benchmark')}>
                📊 View Benchmark Page
              </Button>
              <Button variant="primary" onClick={handleRecertify}>
                🔄 Re-request Certification
              </Button>
            </div>
          </StatusCard>
        </Content>

      </Container>

      {/* Debug Control Panel */}
      {/* <DebugPanel>
        <h4>🎮 Debug Panel - State Controls</h4>
        <div>
          <button onClick={() => changeStatus(STATES.INITIAL)}>1. Initial</button>
          <button onClick={() => changeStatus(STATES.PENDING_ASSIGNMENT)}>2. Pending</button>
        </div>
        <div style={{ marginTop: '5px' }}>
          <button onClick={() => changeStatus(STATES.IN_PROGRESS)}>3. In Progress</button>
          <button onClick={() => changeStatus(STATES.COMPLETED)}>4. Completed</button>
          <button onClick={() => changeStatus(STATES.SUCCESS)}>5. Success</button>
        </div>
        <p>
          Current State: <span>{currentState}</span>
        </p>
        <p>
          Elec: {certificationData.electricity.certified}/{certificationData.electricity.total} | 
          Gas: {certificationData.gas.certified}/{certificationData.gas.total}
        </p>
      </DebugPanel> */}
    </PageWrapper>
    </Spin>
  );
};

export default CertifyPage;


